import { USER_STORAGE } from "constants/index";
import React, { useState, useRef } from "react";
import { Button, message, Popover, List, Typography } from "antd";
import { Presentation, History, ExternalLink } from "lucide-react";
import {
  checkPresentationStatus,
  generetePresentations,
} from "services/ia/ia-service";

const font = '"Montserrat", sans-serif';

const ManagerialBi = () => {
  const [embedUrl] = useState("https://app.powerbi.com/view?r=");
  const [loading, setLoading] = useState(false);

  function getBiToken() {
    const localStorageItem = localStorage.getItem(USER_STORAGE);
    if (localStorageItem) {
      const { biToken } = JSON.parse(localStorageItem);
      return biToken;
    }
    return "";
  }

  const [historyData, setHistoryData] = useState(() =>
    JSON.parse(localStorage.getItem(`bi_presentations_${getBiToken()}`) || "[]"),
  );
  const containerRef = useRef(null);

  const captureDashboard = async () => {
    try {
      // 1. Pede ao usuário para selecionar a aba atual
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" },
      });

      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();

      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // 2. Desenha o frame capturado num canvas
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas
        .getContext("2d")
        .drawImage(video, 0, 0, canvas.width, canvas.height);

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          stream.getTracks().forEach((track) => track.stop());
          resolve(blob);
        }, "image/png");
      });
    } catch (error) {
      console.error("Usuário cancelou ou erro na captura:", error);
      return null;
    }
  };

  const handleGeneratePresentation = async (providerType = "OPENAI") => {
    if (loading) return;
    setLoading(true);

    try {
      const blob = await captureDashboard();
      
      if (!blob) {
        message.warning("Captura de tela cancelada.");
        setLoading(false);
        return;
      }
      
      const formData = new FormData();
      formData.append("file", blob, "dashboard.png");
      formData.append("provider", providerType);

      // 1. Inicia a geração
      message.info("Analisando dashboard e iniciando geração...");
      generetePresentations(formData)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("Erro na resposta do servidor ao gerar apresentação.");
          }
          const data = await response.json();
          pollStatus(data.id);
        })
        .catch((err) => {
          console.error(err);
          message.error("Erro ao iniciar a apresentação.");
          setLoading(false);
        });
    } catch (error) {
      console.error(error);
      message.error("Erro ao iniciar a apresentação.");
      setLoading(false);
    }
  };

  const pollStatus = async (generationId, attempt = 1) => {
    try {
      checkPresentationStatus(generationId)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("Erro ao obter o status da apresentação.");
          }
          const data = await response.json();

          if (data.status === "completed") {
            const pptLink = data.gammaUrl || `https://gamma.app/view/${generationId}`;

            // Salva no histórico
            const history = JSON.parse(
              localStorage.getItem(`bi_presentations_${getBiToken()}`) || "[]",
            );
            history.push({ date: new Date().toISOString(), link: pptLink });
            localStorage.setItem(`bi_presentations_${getBiToken()}`, JSON.stringify(history));
            setHistoryData(history);

            // SUCESSO
            message.success("Apresentação gerada com sucesso!");
            window.open(pptLink, "_blank");
            setLoading(false); // Libera o botão
            
          } else if (data.status === "failed") {
            // FALHA
            message.error("Falha ao gerar apresentação na Gamma App.");
            setLoading(false);
            
          } else {
            // PENDENTE / EM PROGRESSO -> Tenta de novo
            if (attempt > 20) {
              message.warning(
                "A geração está demorando muito. Verifique a Gamma App depois.",
              );
              setLoading(false);
              return;
            }
            
            // Só agenda a próxima checagem se ainda estiver processando
            setTimeout(() => pollStatus(generationId, attempt + 1), 5000);
          }
        })
        .catch((err) => {
          console.error(err);
          message.error("Erro na comunicação com o servidor.");
          setLoading(false);
        });
    } catch (error) {
      console.error("Erro no polling", error);
      message.error("Erro ao checar status da apresentação.");
      setLoading(false);
    }
  };

  const historyContent = (
    <List
      size="small"
      dataSource={[...historyData].reverse()} // Criar cópia para não mutar o original
      renderItem={(item) => (
        <List.Item>
          <Typography.Text
            style={{ fontFamily: font, fontSize: "12px", marginRight: "10px" }}
          >
            {new Date(item.date).toLocaleDateString("pt-BR")}{" "}
            {new Date(item.date).toLocaleTimeString("pt-BR")}
          </Typography.Text>
          <Button
            type="link"
            icon={<ExternalLink size={16} />}
            href={item.link}
            target="_blank"
          >
            Abrir
          </Button>
        </List.Item>
      )}
      style={{ maxHeight: "300px", overflowY: "auto" }}
    />
  );

  return (
    <div className="container-managerial">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
          gap: "10px",
        }}
      >
        <Popover
          content={historyContent}
          title="Histórico de Apresentações"
          trigger="click"
          placement="bottomRight"
        >
          <Button
            icon={
              <History
                size={18}
                style={{ marginBottom: "-3px", marginRight: "5px" }}
              />
            }
            style={{ fontFamily: font }}
          >
            Histórico
          </Button>
        </Popover>
        <Button
          type="primary"
          icon={
            <Presentation
              size={18}
              style={{ marginBottom: "-3px", marginRight: "5px" }}
            />
          }
          onClick={() => handleGeneratePresentation("OPENAI")}
          loading={loading}
          style={{
            fontFamily: font,
            backgroundColor: "#12475B",
            border: "none",
          }}
        >
          Gerar Apresentação IA
        </Button>
      </div>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "calc(100vh - 100px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#fff",
        }}
      >
        <iframe
          title="Hub Data"
          width="100%"
          height="100%"
          src={embedUrl + getBiToken()}
          frameBorder="0"
          allowFullScreen={true}
          style={{ border: "none" }}
        ></iframe>
      </div>
    </div>
  );
};

export default ManagerialBi;